import React, { useState, useEffect } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import axios from 'axios';
import moment from 'moment';
import CallChecklist from './CallChecklist';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Box,
  Flex,
  Text,
  Icon,
  Divider,
  HStack,
  Badge,
  Heading,
  Spacer,
  Grid,
  GridItem,
  Textarea,
  useColorModeValue
} from '@chakra-ui/react';
import { FaPhone, FaCopy, FaWhatsapp, FaHistory } from 'react-icons/fa';
import WhatsAppChat from '../whatsapp/WhatsAppChat';

const RecordCallModal = ({ isOpen, onClose, onSubmit, lead }) => {
  const { socket } = useSocket();
  const [callStatus, setCallStatus] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('');
  const [callNotes, setCallNotes] = useState('');
  const [callHistory, setCallHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [customerNotes, setCustomerNotes] = useState(lead?.customerNotes || '');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const toast = useToast();

  // Color mode values
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const infoBgColor = useColorModeValue('blue.50', 'blue.900');
  const infoTextColor = useColorModeValue('blue.800', 'blue.100');
  const historyBgColor = useColorModeValue('gray.50', 'gray.800');
  const historyItemBgColor = useColorModeValue('white', 'gray.700');
  const historyItemBorderColor = useColorModeValue('gray.100', 'gray.600');
  const historyItemTextColor = useColorModeValue('gray.800', 'white');
  const historyItemMutedColor = useColorModeValue('gray.500', 'gray.400');
  const historyItemMutedColorLight = useColorModeValue('gray.400', 'gray.500');
  
  // Pre-calculate outcome colors
  const answeredBg = useColorModeValue('green.50', 'green.900');
  const answeredBorder = useColorModeValue('green.200', 'green.700');
  const notAnsweredBg = useColorModeValue('red.50', 'red.900');
  const notAnsweredBorder = useColorModeValue('red.200', 'red.700');
  const rescheduledBg = useColorModeValue('blue.50', 'blue.900');
  const rescheduledBorder = useColorModeValue('blue.200', 'blue.700');
  const defaultBg = useColorModeValue('gray.50', 'gray.900');
  const defaultBorder = useColorModeValue('gray.200', 'gray.700');

  // Pure helper functions for getting colors
  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'answered': return 'green';
      case 'not_answered': return 'red';
      case 'rescheduled': return 'blue';
      default: return 'gray';
    }
  };

  const getOutcomeBg = (outcome) => {
    switch (outcome) {
      case 'answered': return answeredBg;
      case 'not_answered': return notAnsweredBg;
      case 'rescheduled': return rescheduledBg;
      default: return defaultBg;
    }
  };

  const getOutcomeBorder = (outcome) => {
    switch (outcome) {
      case 'answered': return answeredBorder;
      case 'not_answered': return notAnsweredBorder;
      case 'rescheduled': return rescheduledBorder;
      default: return defaultBorder;
    }
  };

  // Create API instance with auth token
  const getAPI = () => {
    const token = localStorage.getItem('firebaseToken');
    return axios.create({
      baseURL: process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCALHOST_URL,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  };

  const handleSubmit = () => {
    // Reset form state when modal is closed
    const resetForm = () => {
      setCallStatus('');
      setRescheduleTime('');
      setSelectedPreset('');
      setCallNotes('');
    };

    if (!callStatus) {
      toast({
        title: 'Error',
        description: 'Please select a call status',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    console.log('Call status:', callStatus); // Debug log
    const now = new Date();
    console.log('Current timestamp:', {
      local: now.toString(),
      ISO: now.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: -now.getTimezoneOffset()/60
    });

    let callData = {
      leadId: lead.leadId,
      notes: callNotes.trim() || '',
      timestamp: now.toISOString()
    };

    if (callStatus === 'no') {
      // If call was not picked up, automatically set reschedule time to 5 hours from now
      const now = new Date();
      const nextFollowUp = new Date(now.getTime() + 5 * 60 * 60 * 1000);
      console.log('Next follow up time:', {
        local: nextFollowUp.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
        ISO: nextFollowUp.toISOString(),
        timezone: 'Asia/Kolkata'
      });
      callData = {
        ...callData,
        outcome: 'not_answered',
        rescheduledTo: nextFollowUp.toISOString(),
        notes: callNotes.trim() || `Call not picked up - Auto rescheduled to ${nextFollowUp.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: 'numeric', hour12: true })}`
      };
    } else if (callStatus === 'reschedule') {
      if (!rescheduleTime) {
        toast({
          title: 'Error',
          description: 'Please select a reschedule time',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      callData = {
        ...callData,
        outcome: 'rescheduled',
        rescheduledTo: new Date(rescheduleTime).toISOString(),
        notes: callNotes.trim() || 'Call rescheduled by user'
      };
    } else if (callStatus === 'yes') {
      // Call was picked up
      callData = {
        ...callData,
        outcome: 'answered',
        notes: callNotes.trim() || 'Call completed successfully',
        rescheduledTo: rescheduleTime ? new Date(rescheduleTime).toISOString() : undefined
      };
      // Add follow-up time to notes if set
      if (rescheduleTime) {
        const followUpTime = new Date(rescheduleTime);
        callData.notes += ` - Follow up scheduled for ${followUpTime.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: 'numeric', hour12: true })}`;
      }
    }

    // Always update with nextFollowUpTime where applicable (using lead model field name)
    if (callData.rescheduledTo) {
      // Update lead model's nextFollowUpTime directly
      console.log('[RecordCallModal] Updating lead with nextFollowUpTime:', callData.rescheduledTo);
      onSubmit(callData, lead.leadId, {
        nextFollowUpTime: callData.rescheduledTo
      });
    } else {
      onSubmit(callData);
    }

    // Get current timestamp if one wasn't set
    const callTimestamp = callData.timestamp || new Date().toISOString();

    // Estimate the new call count
    // This is just an estimate - we'll get the real count from the server
    const estimatedCallCount = Number(lead.callCount || 0) + 1;

    // Emit socket event for real-time updates
    console.log('[RecordCallModal] Emitting call:added event:', {
      leadId: lead.leadId,
      timestamp: callTimestamp,
      outcome: callStatus,
      callCount: estimatedCallCount,
      rescheduledTo: callData.rescheduledTo,
      nextFollowUpTime: callData.rescheduledTo  // Include model field explicitly
    });
    
    if (socket) {
      socket.emit('call:added', {
        ...callData,
        leadId: lead.leadId, // Ensure leadId is always included
        timestamp: callTimestamp, // Ensure timestamp is always included
        outcome: callStatus === 'yes' ? 'answered' : callStatus === 'no' ? 'not_answered' : 'rescheduled',
        callCount: estimatedCallCount,
        // Include both rescheduledTo and nextFollowUpTime for clarity
        rescheduledTo: callData.rescheduledTo,
        nextFollowUpTime: callData.rescheduledTo  // Match the lead model field
      });
    } else {
      console.error('[ERROR] Socket connection not available for call:added event');
    }

    resetForm();
    onClose();
  };

  // Fetch call history when modal opens and update customer notes
  useEffect(() => {
    if (isOpen && lead?.leadId) {
      fetchCallHistory();
      setCustomerNotes(lead?.customerNotes || '');
    } else if (!isOpen) {
      // Reset form when modal is closed
      setCallStatus('');
      setRescheduleTime('');
      setCallNotes('');
    }
  }, [isOpen, lead?.leadId]);

  // Save customer notes
  const saveCustomerNotes = async () => {
    if (!lead?.leadId) return;

    setIsSavingNotes(true);
    try {
      const api = getAPI();
      await api.patch(`/api/leads/${lead.leadId}`, { customerNotes });
      toast({
        title: 'Notes saved',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error saving customer notes:', error);
      toast({
        title: 'Error saving notes',
        description: 'Could not save customer notes',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSavingNotes(false);
    }
  };

  const fetchCallHistory = async () => {
    if (!lead?.leadId) return;

    setIsLoading(true);
    try {
      const api = getAPI();
      const response = await api.get(`/api/call-logs/lead/${lead.leadId}`);
      const data = response.data;
      setCallHistory(data.callLogs || []);
    } catch (error) {
      console.error('Error fetching call history:', error);
      toast({
        title: 'Error fetching call history',
        description: 'Could not load previous call logs',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxW="1300px">
        <ModalHeader>
          Record Call {lead.brideAndGroomName ? `- ${lead.brideAndGroomName}` : ''}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex gap={4} h="600px">
            {/* WhatsApp Chat Panel */}
            <Box w="300px" borderRight="1px" borderColor={borderColor} h="100%" overflowY="hidden">
              <WhatsAppChat 
                phoneNumber={lead?.phone?.replace(/[^0-9]/g, '')}
                whatsappGroup={lead?.whatsappGroup}
                whatsappGroupName={lead?.whatsappGroupName}
              />
            </Box>
            
            {/* Call Checklist Panel */}
            <Box w="250px" borderRight="1px" borderColor={borderColor} h="100%" overflowY="auto">
              <CallChecklist />
            </Box>

            {/* Main Content Panel */}
            <VStack flex={1} spacing={4} align="stretch" h="100%" overflowY="auto">
              {/* Essential Lead Information */}
              {lead && (
                <Box p={3} borderWidth="1px" borderRadius="md" bg={bgColor} w="100%">
                  <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={3}>
                    {lead.pointOfContact && (
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color={textColor}>Point of Contact</Text>
                        <Text fontSize="sm">{lead.pointOfContact}</Text>
                      </Box>
                    )}
                    {lead.eventDates && lead.eventDates.length > 0 && (
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color={textColor}>Wedding Date</Text>
                        <Text fontSize="sm">{new Date(lead.eventDates[0]).toLocaleDateString()}</Text>
                      </Box>
                    )}
                    {lead.budget && lead.budget.amount && (
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color={textColor}>Budget</Text>
                        <Text fontSize="sm">{lead.budget.currency} {lead.budget.amount.toLocaleString()}</Text>
                      </Box>
                    )}
                    {lead.guestCapacity && (
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color={textColor}>Guest Count</Text>
                        <Text fontSize="sm">{lead.guestCapacity}</Text>
                      </Box>
                    )}
                    {lead.leadSource && (
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color={textColor}>Lead Source</Text>
                        <Text fontSize="sm">{lead.leadSource}</Text>
                      </Box>
                    )}
                    {lead.locations && lead.locations.cities && lead.locations.cities.length > 0 && (
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color={textColor}>Location</Text>
                        <Text fontSize="sm">{lead.locations.cities.join(', ')}</Text>
                      </Box>
                    )}
                  </Grid>
                  
                  {/* Customer Notes - Full Width */}
                  <Box mt={2}>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" fontWeight="bold" color={textColor}>Customer Notes</Text>
                      <Button 
                        size="xs" 
                        colorScheme="blue" 
                        onClick={saveCustomerNotes}
                        isLoading={isSavingNotes}
                      >
                        Save Notes
                      </Button>
                    </HStack>
                    <Textarea
                      value={customerNotes}
                      onChange={(e) => setCustomerNotes(e.target.value)}
                      placeholder="Enter customer notes here..."
                      size="sm"
                      rows={2}
                      maxH="100px"
                      overflowY="auto"
                      resize="vertical"
                      bg={bgColor}
                      borderColor={borderColor}
                    />
                  </Box>
                </Box>
              )}

              {/* Call Details and History */}
              <Grid templateColumns="300px 1fr" gap={4} flex={1} minH={0}>
                {/* Phone and Call Status */}
                <VStack spacing={4} align="stretch">
                  {lead.phone && (
                    <Box 
                      p={4} 
                      borderRadius="md" 
                      borderWidth="1px" 
                      borderColor="blue.200"
                      bg={infoBgColor}
                    >
                      <FormLabel fontSize="sm" mb={1} color={infoTextColor}>Phone Number</FormLabel>
                      <Flex 
                        align="center" 
                        justify="space-between"
                        onClick={() => {
                          const phoneNumber = lead.phone.startsWith('+91') ? lead.phone.substring(3) : lead.phone;
                          navigator.clipboard.writeText(phoneNumber);
                          toast({
                            title: "Phone number copied",
                            status: "success",
                            duration: 2000,
                          });
                        }}
                        cursor="pointer"
                        _hover={{ bg: "blue.100" }}
                        p={2}
                        borderRadius="md"
                        transition="all 0.2s"
                      >
                        <Flex align="center">
                          <Icon as={FaPhone} color="blue.500" mr={3} />
                          <Text fontSize="xl" fontWeight="bold" letterSpacing="wider">
                            {lead.phone.startsWith('+91') ? lead.phone.substring(3) : lead.phone}
                          </Text>
                        </Flex>
                        <Flex>
                          <Icon 
                            as={FaWhatsapp} 
                            color="green.500" 
                            fontSize="xl" 
                            mr={3}
                            cursor="pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              const phoneWithPrefix = lead.phone.startsWith('+91') ? lead.phone : `+91${lead.phone}`;
                              const cleanPhone = phoneWithPrefix.replace(/\s+/g, '');
                              window.open(`https://wa.me/${cleanPhone.substring(1)}`, '_blank');
                            }}
                          />
                          <Icon as={FaCopy} color="blue.500" />
                        </Flex>
                      </Flex>
                      
                      {lead.whatsappGroup && (
                        <Box 
                          mt={2} 
                          p={2} 
                          borderWidth="1px" 
                          borderRadius="md" 
                          borderColor="green.200"
                          bg="green.50"
                        >
                          <Text fontSize="xs" fontWeight="bold" color="green.700" mb={1}>WhatsApp Group</Text>
                          <Text fontSize="sm" fontWeight="medium" color="green.800">{lead.whatsappGroupName}</Text>
                          <Text fontSize="xs" color="green.600" mt={1} fontFamily="mono">{lead.whatsappGroup}</Text>
                        </Box>
                      )}
                    </Box>
                  )}
                
                  <FormControl>
                    <FormLabel>Did the user pick up the call?</FormLabel>
                    <RadioGroup value={callStatus} onChange={(val) => {
                      console.log('Selected value:', val);
                      setCallStatus(val);
                      setRescheduleTime('');
                      setSelectedPreset('');
                    }}>
                      <VStack align="start" spacing={2}>
                        <Radio value="yes">Yes</Radio>
                        <Radio value="no">No (Will reschedule to 5 hours later)</Radio>
                        <Radio value="reschedule">Reschedule to specific time</Radio>
                      </VStack>
                    </RadioGroup>
                  </FormControl>

                  {(callStatus === 'reschedule' || callStatus === 'yes') && (
                    <FormControl>
                      <FormLabel>
                        {callStatus === 'yes' ? 'Next follow-up time' : 'Select reschedule time'}
                      </FormLabel>
                      <VStack spacing={2} align="stretch">
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            colorScheme={selectedPreset === '2h' ? 'blue' : 'gray'}
                            onClick={() => {
                              const now = new Date();
                              const time = new Date(now.getTime() + 2 * 60 * 60 * 1000);
                              setRescheduleTime(time.toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).slice(0, 16));
                              setSelectedPreset('2h');
                            }}
                          >
                            +2h
                          </Button>
                          <Button
                            size="sm"
                            colorScheme={selectedPreset === '5h' ? 'blue' : 'gray'}
                            onClick={() => {
                              const now = new Date();
                              const time = new Date(now.getTime() + 5 * 60 * 60 * 1000);
                              setRescheduleTime(time.toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).slice(0, 16));
                              setSelectedPreset('5h');
                            }}
                          >
                            +5h
                          </Button>
                          <Button
                            size="sm"
                            colorScheme={selectedPreset === '1d' ? 'blue' : 'gray'}
                            onClick={() => {
                              const now = new Date();
                              const time = new Date(now.getTime() + 24 * 60 * 60 * 1000);
                              setRescheduleTime(time.toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).slice(0, 16));
                              setSelectedPreset('1d');
                            }}
                          >
                            +1d
                          </Button>
                        </HStack>
                        <Input
                          type="datetime-local"
                          value={rescheduleTime}
                          onChange={(e) => {
                            setRescheduleTime(e.target.value);
                            setSelectedPreset('');
                          }}
                          min={new Date().toISOString().slice(0, 16)}
                          color={textColor}
                          bg={bgColor}
                          borderColor={borderColor}
                        />
                      </VStack>
                    </FormControl>
                  )}
                  
                  {callStatus && (
                    <FormControl mt={3}>
                      <FormLabel>Call Notes</FormLabel>
                      <Textarea
                        placeholder="Enter notes about the call..."
                        value={callNotes}
                        onChange={(e) => setCallNotes(e.target.value)}
                        rows={3}
                        resize="vertical"
                        color={textColor}
                        bg={bgColor}
                        borderColor={borderColor}
                      />
                    </FormControl>
                  )}
                </VStack>

                {/* Call History */}
                <Box>
                  <Flex align="center" mb={2}>
                    <Icon as={FaHistory} color="blue.500" mr={2} fontSize="lg" />
                    <Heading size="md">Call History</Heading>
                  </Flex>
                  
                  <Box maxH="100%" overflowY="auto" pr={2}>
                    {isLoading ? (
                      <Flex justify="center" py={4}>
                        <Text fontSize="md">Loading call history...</Text>
                      </Flex>
                    ) : callHistory.length === 0 ? (
                      <Box p={4} borderWidth={1} borderRadius="md" borderStyle="dashed">
                        <Text color="gray.500" textAlign="center" fontSize="md">No previous calls</Text>
                      </Box>
                    ) : (
                      <VStack spacing={2} align="stretch">
                        {callHistory.map((call) => (
                          <Box
                            key={call._id}
                            p={2}
                            borderWidth={1}
                            borderRadius="md"
                            borderColor={getOutcomeBorder(call.outcome)}
                            bg={getOutcomeBg(call.outcome)}
                          >
                            <HStack justify="space-between" mb={1}>
                              <Badge colorScheme={getOutcomeColor(call.outcome)} fontSize="sm" p={1}>
                                {call.outcome === 'answered' ? 'Answered' : 
                                 call.outcome === 'not_answered' ? 'Not Answered' : 
                                 call.outcome === 'rescheduled' ? 'Rescheduled' : call.outcome}
                              </Badge>
                              <Text fontSize="sm" color={historyItemMutedColor} fontWeight="medium">
                                {call.calledBy?.email ? call.calledBy.email.split('@')[0] : 'Unknown'}
                              </Text>
                            </HStack>
                            
                            <Box 
                              p={2} 
                              bg={historyItemBgColor} 
                              borderRadius="sm" 
                              mb={2} 
                              borderWidth="1px" 
                              borderColor={historyItemBorderColor}
                            >
                              <Text fontSize="sm" color={historyItemTextColor}>{call.notes || 'No notes'}</Text>
                            </Box>
                            
                            <HStack justify="space-between" fontSize="sm" color={historyItemMutedColor}>
                              <VStack spacing={0} align="start">
                                <Text fontWeight="medium">{new Date(call.timestamp).toLocaleString()}</Text>
                                <Text fontSize="xs" color={historyItemMutedColorLight}>{moment(call.timestamp).fromNow()}</Text>
                              </VStack>
                              {call.rescheduledTo && (
                                <VStack spacing={0} align="end">
                                  <Text color={textColor} fontWeight="medium">
                                    Next: {new Date(call.rescheduledTo).toLocaleString()}
                                  </Text>
                                  <Text fontSize="xs" color={historyItemMutedColor}>{moment(call.rescheduledTo).fromNow()}</Text>
                                </VStack>
                              )}
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>
                </Box>
              </Grid>
            </VStack>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSubmit}>
            Submit
          </Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RecordCallModal;
